import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Target, TrendUp, Brain, Warning, Scales, ArrowRight, CheckCircle, XCircle, BookOpen, Percent, Lightning } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const WinRateTradingPT = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Win Rate no Trading | O Que é e Por Que Engana',
      description: 'Descubra o que é win rate no trading e por que ele pode ser enganoso se analisado sozinho.',
      path: '/pt/win-rate-trading',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/win-rate-trading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Métricas de Trading</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-4">
          <Percent size={16} weight="bold" />
          <span>Métricas de Trading</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          O Que é Win Rate no Trading
        </h1>

        <p className="text-xl text-gray-400 mb-12">
          (E Por Que Pode Enganar)
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Pergunte a qualquer trader iniciante qual métrica ele mais acompanha e a resposta provavelmente será: <strong className="text-white">"win rate"</strong>. A lógica parece óbvia: quanto mais trades você acerta, melhor. Certo?
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            <strong className="text-amber-400">Não necessariamente.</strong> O win rate é uma das métricas mais mal compreendidas do trading. Ele pode te dar uma falsa sensação de segurança enquanto sua conta sangra. Ou te fazer duvidar de uma estratégia que é, na verdade, lucrativa.
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Percent size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">O Que é Win Rate</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Win rate é simplesmente o percentual de trades vencedores em relação ao total de trades. É uma medida de frequência de acerto.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-8 mb-6">
              <h3 className="font-semibold text-amber-400 mb-4 text-center">Fórmula do Win Rate</h3>
              <div className="bg-gray-900/50 rounded-lg p-6 text-center">
                <p className="text-2xl font-mono text-white">
                  Win Rate = (<span className="text-green-400">Trades Vencedores</span> / <span className="text-blue-400">Total de Trades</span>) × 100
                </p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">Exemplo Prático:</h3>
              <div className="grid sm:grid-cols-3 gap-4 text-center mb-4">
                <div className="bg-green-500/10 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Trades vencedores</p>
                  <p className="text-2xl font-bold text-green-400">7</p>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Total de trades</p>
                  <p className="text-2xl font-bold text-blue-400">10</p>
                </div>
                <div className="bg-amber-500/10 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Win Rate</p>
                  <p className="text-2xl font-bold text-amber-400">70%</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm text-center">
                Win Rate = (7 / 10) × 100 = <strong className="text-white">70%</strong>
              </p>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Simples, direto, fácil de calcular. Mas essa simplicidade esconde uma armadilha perigosa.
            </p>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Target size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Win Rate é Tão Popular</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Existe uma razão pela qual o win rate domina as conversas sobre trading. Ele apela para instintos humanos básicos.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Fácil de Entender</h3>
                <p className="text-gray-400 text-sm">"70% de acerto" é intuitivo. Qualquer pessoa entende. Não precisa de matemática complexa.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Gera Confiança</h3>
                <p className="text-gray-400 text-sm">Um número alto de acertos faz você se sentir competente. É validação imediata do seu esforço.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Sensação de Controle</h3>
                <p className="text-gray-400 text-sm">"Se eu acertar mais, vou ganhar mais." Parece lógico. Parece algo que você pode controlar.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O problema é que essa lógica simplista ignora a outra metade da equação: <strong className="text-white">quanto você ganha quando acerta versus quanto perde quando erra.</strong>
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Win Rate Pode Enganar</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O win rate sozinho não diz nada sobre lucratividade. E esse é o ponto crucial que a maioria dos traders não entende.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Ignora o Risk Reward</h3>
                </div>
                <p className="text-gray-400 text-sm">Um win rate de 80% significa nada se cada ganho é R$ 50 e cada perda é R$ 300. A matemática não fecha.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Ignora o Tamanho das Perdas</h3>
                </div>
                <p className="text-gray-400 text-sm">Você pode acertar 9 de 10 trades e perder dinheiro se aquele 1 trade perdedor for uma catástrofe.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Pode Esconder uma Estratégia Ruim</h3>
                </div>
                <p className="text-gray-400 text-sm">Estratégias que cortam lucro cedo e deixam prejuízo correr têm win rate alto — e são desastrosas.</p>
              </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <p className="text-gray-300">
                <strong className="text-amber-400">A verdade incômoda:</strong> Win rate alto pode ser sinal de que você está fechando ganhos cedo demais (medo de perder lucro) e segurando perdas tempo demais (esperança de recuperação).
              </p>
            </div>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Scales size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Alto Win Rate vs Baixo Win Rate</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Vamos comparar dois traders com win rates completamente diferentes e ver quem realmente ganha dinheiro.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-red-400 mb-4">Trader A: Win Rate 80%</h3>
                <ul className="text-gray-400 text-sm space-y-2 mb-4">
                  <li>• 10 trades no mês</li>
                  <li>• 8 vencedores, 2 perdedores</li>
                  <li>• Ganho médio: <span className="text-green-400">R$ 100</span></li>
                  <li>• Perda média: <span className="text-red-400">R$ 500</span></li>
                </ul>
                <div className="bg-gray-900/50 rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-500">Resultado</p>
                  <p className="text-xl font-bold text-red-400">-R$ 200</p>
                  <p className="text-xs text-gray-500 mt-1">(8 × 100) - (2 × 500)</p>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6">
                <h3 className="font-semibold text-[#00FF85] mb-4">Trader B: Win Rate 40%</h3>
                <ul className="text-gray-400 text-sm space-y-2 mb-4">
                  <li>• 10 trades no mês</li>
                  <li>• 4 vencedores, 6 perdedores</li>
                  <li>• Ganho médio: <span className="text-green-400">R$ 400</span></li>
                  <li>• Perda média: <span className="text-red-400">R$ 100</span></li>
                </ul>
                <div className="bg-gray-900/50 rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-500">Resultado</p>
                  <p className="text-xl font-bold text-[#00FF85]">+R$ 1.000</p>
                  <p className="text-xs text-gray-500 mt-1">(4 × 400) - (6 × 100)</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O Trader A tem o dobro do win rate e <strong className="text-white">perde dinheiro</strong>. O Trader B erra mais do que acerta e é <strong className="text-white">extremamente lucrativo</strong>. Isso deveria mudar como você pensa sobre win rate.
            </p>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <TrendUp size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Relação com Risk Reward</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Win rate e risk reward são duas faces da mesma moeda. Você não pode analisar um sem o outro.
            </p>

            <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-[#00FF85] mb-4">A Matemática do Equilíbrio:</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 text-gray-400">Risk Reward</th>
                      <th className="text-left py-2 text-gray-400">Win Rate Necessário</th>
                      <th className="text-left py-2 text-gray-400">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800">
                      <td className="py-2">1:1</td>
                      <td className="py-2">&gt; 50%</td>
                      <td className="py-2 text-amber-400">Exigente</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2">1:2</td>
                      <td className="py-2">&gt; 33%</td>
                      <td className="py-2 text-[#00FF85]">Confortável</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2">1:3</td>
                      <td className="py-2">&gt; 25%</td>
                      <td className="py-2 text-[#00FF85]">Favorável</td>
                    </tr>
                    <tr>
                      <td className="py-2">1:4</td>
                      <td className="py-2">&gt; 20%</td>
                      <td className="py-2 text-[#00FF85]">Muito favorável</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Os dois precisam andar juntos. O equilíbrio entre win rate e risk reward é o que determina se você ganha ou perde dinheiro no longo prazo.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Armadilha Psicológica</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A obsessão por win rate alto é frequentemente um sintoma de problemas psicológicos mais profundos no trading.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Medo de Perder</h3>
                <p className="text-gray-400 text-sm">Perder dói. E para evitar essa dor, você fecha lucros rápido (garantindo o ganho) e segura perdas (esperando voltar). Isso infla o win rate enquanto destrói a conta.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Fechar Lucro Cedo</h3>
                <p className="text-gray-400 text-sm">Você tem +R$ 100, o alvo era +R$ 300. Mas fecha porque "melhor garantir". Seu win rate sobe, mas seu ganho médio despenca.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Buscar "Acertar Sempre"</h3>
                <p className="text-gray-400 text-sm">A necessidade de estar certo é um dos maiores destruidores de contas. Trading não é sobre estar certo — é sobre ganhar dinheiro.</p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <BookOpen size={28} className="text-[#00FF85] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">"Trading in the Zone" — Mark Douglas</h3>
                  <p className="text-gray-400 text-sm">
                    Douglas explica que traders de sucesso não se importam em estar certos. Eles se importam em <strong className="text-white">seguir o processo</strong>. O resultado de um trade individual é irrelevante — o que importa é a execução consistente ao longo do tempo.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Erros Comuns na Interpretação</h2>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Olhar Só Win Rate</h3>
                </div>
                <p className="text-gray-400 text-sm">Analisar win rate isoladamente é como julgar um filme apenas pela duração. Falta contexto. Falta informação crucial.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Ignorar Contexto</h3>
                </div>
                <p className="text-gray-400 text-sm">Seu win rate em tendência pode ser 60%, em lateralidade 30%. Se você mistura, perde informação valiosa sobre onde performa melhor.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Basear Decisões em Poucos Trades</h3>
                </div>
                <p className="text-gray-400 text-sm">Um win rate de 90% com 10 trades é ruído estatístico. Você precisa de volume para confiar no número.</p>
              </div>
            </div>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <ChartLine size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Que Importa Mais que Win Rate</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Se win rate não é a métrica definitiva, o que você deveria acompanhar?
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Profit Factor</h3>
                <p className="text-gray-400 text-sm">Lucro total dividido por prejuízo total. Mostra quanto você ganha para cada R$ 1 que perde. Essa é a métrica de qualidade real.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Consistência</h3>
                <p className="text-gray-400 text-sm">Resultados estáveis ao longo do tempo. Uma curva de capital suave indica um sistema robusto.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Execução</h3>
                <p className="text-gray-400 text-sm">Você está seguindo suas próprias regras? A execução perfeita é mais importante que o resultado de um trade individual.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Análise de Dados</h3>
                <p className="text-gray-400 text-sm">Entender onde você ganha e onde você perde. Quais setups funcionam, quais não. Dados guiam decisões melhores.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Win rate é uma peça do quebra-cabeça, não a imagem completa. Combine-o com outras métricas para ter uma visão real.
            </p>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Target size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Como Usar Win Rate da Forma Correta</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Win rate não é inútil — só precisa ser usado corretamente, como parte de um sistema maior.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">Uso correto do Win Rate:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Sempre combine com Risk Reward:</strong> Win rate de 60% com RR 1:1 é diferente de 60% com RR 1:0.5.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Segmente por setup/ativo:</strong> Seu win rate geral pode esconder informações importantes.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Use amostra significativa:</strong> Pelo menos 30-50 trades antes de tirar conclusões.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Não otimize para win rate:</strong> Otimize para lucro. Win rate é consequência, não objetivo.</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O win rate funciona como parte de um sistema de análise completo, nunca isoladamente.
            </p>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Analise Seu Win Rate com Contexto</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam a analisar win rate junto com outras métricas, oferecendo uma visão mais completa da performance. Entenda não apenas quantos trades você acerta, mas se isso está gerando lucro real.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Entenda Seu Win Rate de Verdade</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Entender seu win rate corretamente é essencial para tomar decisões melhores no trading. Não caia na armadilha de otimizar para a métrica errada.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Win rate alto é sempre bom?</h3>
                <p className="text-gray-400 text-sm">Não. Win rate alto pode esconder uma estratégia que corta lucros cedo e deixa prejuízos correrem. Você pode ter 80% de acerto e perder dinheiro se o tamanho dos gains for muito menor que o dos losses.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Dá pra lucrar com win rate baixo?</h3>
                <p className="text-gray-400 text-sm">Sim, absolutamente. Muitos traders profissionais têm win rate de 30-40% e são extremamente lucrativos. O segredo está no risk reward: quando acertam, ganham muito mais do que perdem quando erram.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">O que é mais importante que win rate?</h3>
                <p className="text-gray-400 text-sm">Profit factor (lucro total / prejuízo total) é mais revelador. Também: consistência da curva de capital, qualidade de execução, e o equilíbrio entre win rate e risk reward.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como melhorar win rate?</h3>
                <p className="text-gray-400 text-sm">Melhore a seleção de trades (só entre nos melhores setups), o timing das entradas, e a leitura de contexto de mercado. Mas cuidado: não sacrifique seu risk reward para inflar o win rate.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/profit-factor-trading"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Profit Factor no Trading</h3>
                <p className="text-gray-500 text-sm">Entenda a métrica que realmente mostra a qualidade da sua estratégia.</p>
              </Link>

              <Link
                to="/pt/como-melhorar-risk-reward"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Melhorar Risk Reward</h3>
                <p className="text-gray-500 text-sm">Técnicas para melhorar sua relação risco/retorno.</p>
              </Link>

              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada.</p>
              </Link>

              <Link
                to="/pt/por-que-traders-nao-evoluem"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Por Que Traders Não Evoluem</h3>
                <p className="text-gray-500 text-sm">Entenda os erros comportamentais que impedem evolução.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WinRateTradingPT;
